import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'jotai';
import { useUsersEssentialsQueryWithParam } from 'hooks';
import { UserEssentials } from 'Types/User.types';
import { AvatarAndTitle, TreeNode } from 'Components/Primitives';
import { ConditionData } from 'Components/Conditions';
import { UsersTreeNodesAtom } from 'Components/Users/UsersTreeSelect/UsersTreeAtom';
import { useEnhanceUsersWithManagers } from 'Components/Users/UsersTreeSelect/hooks/useEnhanceUsersWithManagers';

type DescendantCountMap = Map<TreeNode, number>;

const countDescendantsMemoized = (node: TreeNode | null, map: DescendantCountMap): number => {
  if (!node) return 0;
  if (map.has(node)) return map.get(node)!;

  const directChildrenCount = node.children?.length ?? 0;
  if (directChildrenCount === 0) {
    map.set(node, 0);
    return 0;
  }

  let totalDescendants = directChildrenCount;
  if (node.children) {
    for (const child of node.children) {
      totalDescendants += countDescendantsMemoized(child, map);
    }
  }

  map.set(node, totalDescendants);
  return totalDescendants;
};

export const useUsersTreeNodes = (): {
  getUserTreeNodes(keyAndConditions: { key: string; conditions: ConditionData[] } | null): Promise<TreeNode[]>;
} => {
  const { t } = useTranslation(['common']);
  const atomStore = useStore();

  const { getUsersWithManagers } = useEnhanceUsersWithManagers();
  const { fetch } = useUsersEssentialsQueryWithParam();

  const getBaseTreeNodes = useCallback((data: UserEssentials[]): TreeNode[] => {
    const nodeMap = new Map<string | number, TreeNode>();
    const roots: TreeNode[] = [];

    data.forEach((userEssential) => {
      const userId = userEssential.user.value;
      if (!nodeMap.has(userId)) {
        nodeMap.set(userId, {
          value: userId,
          title: (
            <AvatarAndTitle
              size="small"
              title={userEssential.user.data.fullName || ''}
              avatarSrc={userEssential.user.data.avatar}
            />
          ),
          searchValue: userEssential.user.data.fullName || '',
          selectable: true,
          children: []
        });
      }
    });

    data.forEach((userEssential) => {
      const userId = userEssential.user.value;
      const managerId = userEssential.manager?.value || null;
      const node = nodeMap.get(userId);

      if (!node) {
        console.warn(`Node for user ${userId} not found in map during linking.`);
        return;
      }

      if (managerId === null) {
        if (!roots.some((root) => root.value === node.value)) {
          roots.push(node);
        }
      } else {
        const managerNode = nodeMap.get(managerId);
        if (managerNode) {
          if (!managerNode.children.some((child) => child.value === node.value)) {
            managerNode.children.push(node);
          }
        } else {
          if (!roots.some((root) => root.value === node.value)) {
            roots.push(node);
          }
        }
      }
    });
    return roots;
  }, []);

  const getDescendantCountMap = useCallback((baseTree: TreeNode[]): DescendantCountMap => {
    const map: DescendantCountMap = new Map();
    if (baseTree) {
      baseTree.forEach((root) => {
        countDescendantsMemoized(root, map);
      });
    }
    return map;
  }, []);

  const applyTeamViewTransformation = useCallback(
    (originalNode: TreeNode, dcm: DescendantCountMap): TreeNode => {
      const sortedOriginalChildren = [...originalNode.children].sort((a, b) => {
        const countA = dcm.get(a) ?? 0;
        const countB = dcm.get(b) ?? 0;
        return countB - countA;
      });

      const transformedAndSortedChildren = sortedOriginalChildren.map((child) =>
        applyTeamViewTransformation(child, dcm)
      );

      if (originalNode.children && originalNode.children.length > 0) {
        const managerAsIndividualInTeam: TreeNode = {
          ...originalNode,
          selectable: true,
          children: []
        };

        return {
          ...originalNode,
          title: (
            <span style={{ display: 'inline-block', lineHeight: 1.3, verticalAlign: 'middle' }}>
              {t('team', { name: originalNode.searchValue })}
            </span>
          ),
          value: t('team', { name: `${originalNode.searchValue}-${originalNode.value}` }),
          searchValue: t('team', { name: originalNode.searchValue }),
          selectable: false,
          children: [managerAsIndividualInTeam, ...transformedAndSortedChildren]
        };
      }

      return { ...originalNode, children: transformedAndSortedChildren };
    },
    [t]
  );

  const getUserTreeNodes = useCallback(
    async (keyAndConditions: { key: string; conditions: ConditionData[] } | null) => {
      const atomKey = keyAndConditions ? keyAndConditions.key : 'all';
      const atomData = atomStore.get(UsersTreeNodesAtom({ key: atomKey }));
      if (atomData) return atomData;

      const data = await fetch(keyAndConditions?.conditions || null);
      const userList = keyAndConditions ? await getUsersWithManagers(data.data) : data.data;

      const baseTree = getBaseTreeNodes(userList);
      const descendantCountMap = getDescendantCountMap(baseTree);

      const sortedRoots = [...baseTree].sort((a, b) => {
        const countA = descendantCountMap.get(a) ?? 0;
        const countB = descendantCountMap.get(b) ?? 0;
        return countB - countA;
      });

      const finalNodes = sortedRoots.map((rootNode) => applyTeamViewTransformation(rootNode, descendantCountMap));
      atomStore.set(UsersTreeNodesAtom({ key: atomKey }), finalNodes);

      return finalNodes;
    },
    [atomStore, fetch, getBaseTreeNodes, getDescendantCountMap, applyTeamViewTransformation, getUsersWithManagers]
  );

  return useMemo(() => ({ getUserTreeNodes }), [getUserTreeNodes]);
};
